// Street View pegman: the side-effecting half (Google script, Map Tiles
// session, Mapbox layer). Policy lives in helpers/streetView.ts.
//
// The browser key is a public, referrer-restricted key, baked in at build time
// like VITE_MAPBOX_ACCESS_TOKEN. It is allowed the Maps JavaScript API (the
// panorama viewer and panorama lookup) and the Map Tiles API (the coverage
// overlay) only. Without it the pegman is simply not offered.

import {
  COVERAGE_LAYER_ID,
  COVERAGE_SOURCE_ID,
  type CoverageSession,
  coverageTileUrl,
  createSessionRequest,
  isSessionFresh,
  parseSessionResponse,
} from './helpers/streetView';
import { reportHandledError } from './bugsnag';

export const GOOGLE_MAPS_JS_KEY: string = import.meta.env.VITE_GOOGLE_MAPS_JS_KEY || '';

export function isStreetViewAvailable(): boolean {
  return GOOGLE_MAPS_JS_KEY.length > 0;
}

/* The Maps JS API has no bundled types here; this is the slice we touch. */
type GoogleMaps = any;

let googleMapsPromise: Promise<GoogleMaps> | null = null;

export function loadGoogleMaps(): Promise<GoogleMaps> {
  const w = window as any;
  if (w.google?.maps?.StreetViewPanorama) return Promise.resolve(w.google.maps);
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const callbackName = '__hiikerGoogleMapsReady';
    w[callbackName] = () => {
      delete w[callbackName];
      resolve(w.google.maps);
    };

    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js'
      + `?key=${encodeURIComponent(GOOGLE_MAPS_JS_KEY)}&v=weekly&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => {
      // Let the next drop try again rather than caching the failure forever.
      googleMapsPromise = null;
      reject(new Error('Google Maps JavaScript API failed to load'));
    };
    document.head.appendChild(script);
  });
  return googleMapsPromise;
}

let session: CoverageSession | null = null;
let sessionPromise: Promise<CoverageSession | null> | null = null;

async function coverageSession(): Promise<CoverageSession | null> {
  if (isSessionFresh(session, Date.now())) return session;
  if (sessionPromise) return sessionPromise;

  const { url, body } = createSessionRequest(GOOGLE_MAPS_JS_KEY);
  sessionPromise = fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      const parsed = parseSessionResponse(await response.json().catch(() => null));
      if (!parsed) throw new Error(`Map Tiles createSession failed (HTTP ${response.status})`);
      session = parsed;
      return parsed;
    })
    .catch((error) => {
      // The pegman still works without the blue lines; it just drops blind.
      reportHandledError(error, { feature: 'street_view_coverage' });
      return null;
    })
    .finally(() => { sessionPromise = null; });

  return sessionPromise;
}

/**
 * Paint the coverage over the map. Re-adds the source when missing because a
 * basemap style switch wipes every custom source and layer.
 */
export async function showCoverage(map: any): Promise<void> {
  const current = await coverageSession();
  if (!current || !map?.getStyle) return;

  const tiles = [coverageTileUrl(current.token, GOOGLE_MAPS_JS_KEY)];
  const source = map.getSource(COVERAGE_SOURCE_ID);
  if (source && source.tiles?.[0] !== tiles[0]) {
    // The session renewed since the source was added: tiles keyed to the old token would 403.
    if (map.getLayer(COVERAGE_LAYER_ID)) map.removeLayer(COVERAGE_LAYER_ID);
    map.removeSource(COVERAGE_SOURCE_ID);
  }

  if (!map.getSource(COVERAGE_SOURCE_ID)) {
    map.addSource(COVERAGE_SOURCE_ID, {
      type: 'raster',
      tiles,
      tileSize: 256,
      maxzoom: 22,
      attribution: '© Google',
    });
  }
  if (!map.getLayer(COVERAGE_LAYER_ID)) {
    map.addLayer({
      id: COVERAGE_LAYER_ID,
      type: 'raster',
      source: COVERAGE_SOURCE_ID,
      paint: { 'raster-opacity': 0.9, 'raster-fade-duration': 0 },
    });
  }
}

export function hideCoverage(map: any): void {
  if (map?.getLayer?.(COVERAGE_LAYER_ID)) map.removeLayer(COVERAGE_LAYER_ID);
}

export interface FoundPanorama {
  panoId: string;
  lat: number;
  lng: number;
}

/** The nearest outdoor panorama within `radius` metres, or null when Google has none. */
export async function findPanorama(lat: number, lng: number, radius: number): Promise<FoundPanorama | null> {
  const maps = await loadGoogleMaps();
  const service = new maps.StreetViewService();
  try {
    const { data } = await service.getPanorama({
      location: { lat, lng },
      radius,
      source: maps.StreetViewSource.OUTDOOR,
      preference: maps.StreetViewPreference.NEAREST,
    });
    const location = data?.location;
    if (!location?.pano) return null;
    return { panoId: location.pano, lat: location.latLng.lat(), lng: location.latLng.lng() };
  } catch (error: any) {
    // ZERO_RESULTS is the ordinary "no imagery here" answer, not a fault.
    if (error?.code === maps.StreetViewStatus?.ZERO_RESULTS) return null;
    throw error;
  }
}
